import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useDataTable } from '@onward-delivery/design';
import { fleetColumns } from '@/components/Resources/Fleet/columns';
import { subregionLabelsById } from '@/components/Resources/Fleet/utils';
import { GET_COVERAGE_CAPACITY } from './graphql';
import { coverageSummary, profilePlacements, regionRows } from './utils';

// Edit opens the resource dialog, which writes; Hours is off by default on Fleet too.
const VIEW_ONLY_HIDDEN_COLUMNS = new Set(['edit', 'timeframe']);

// Stable while loading, so the table does not see a new array every render.
const NO_RESOURCES = [];

export const useCoverageCapacity = (carrierId, organizationId, locations) => {
    const { data, error } = useQuery(GET_COVERAGE_CAPACITY, {
        variables: { carrier_id: carrierId, organization_id: organizationId },
        skip: !carrierId || !organizationId,
        fetchPolicy: 'cache-and-network',
        onError: (e) => Sentry.captureException(e),
    });

    const coverage = useMemo(() => {
        if (!data) return null;

        const summary = coverageSummary(data);
        const regions = regionRows(data.subregions, locations);
        const placements = profilePlacements(data.delivery_profiles, carrierId);

        return { summary, regions, placements };
    }, [data, carrierId, locations]);

    const resourceColumns = useMemo(() => {
        const subregionLabels = subregionLabelsById(data?.subregions);
        const columns = fleetColumns({ subregionLabels });

        return columns.filter((column) => !VIEW_ONLY_HIDDEN_COLUMNS.has(column.id));
    }, [data]);

    const resourceTable = useDataTable({
        columns: resourceColumns,
        data: data?.resources ?? NO_RESOURCES,
        getRowId: (row) => row.resource_id,
    });

    return { coverage, error, resourceTable };
};
